import React from 'react';
import { List, Avatar, Tooltip } from 'antd';
import { CheckOutlined, UserOutlined } from '@ant-design/icons';
import styles from '../../layouts/mainLayout.module.scss';

const AVATAR_PARAM_KEYS = new Set([
  'UserAvatar',
  'UserAvatarUrl',
  'AvatarUrl',
  'ProfileImageUrl',
  'UserPhotoUrl',
]);

function getNotificationAvatarSrc(
  parameters?: Array<{ key?: string; value?: string }>
): string | undefined {
  const param = parameters?.find(
    (p) => p?.value && p?.key && AVATAR_PARAM_KEYS.has(p.key)
  );
  return param?.value || undefined;
}

interface NotificationResultItemProps {
  item: {
    id: number;
    notificationText?: string;
    parameters?: Array<{
      key: string;
      value: string;
      target?: string;
    }>;
    isRead: boolean;
    createdAt: string;
  };
  onMarkAsRead: (itemId: number) => void;
  markingAsRead?: boolean;
  onEntryNameClick: (target: string, notificationId: number, e: React.MouseEvent) => void;
}

const NotificationResultItem: React.FC<NotificationResultItemProps> = ({
  item,
  onMarkAsRead,
  markingAsRead = false,
  onEntryNameClick,
}) => {
  const  renderNotificationText = (notification: any) => {
    if (!notification.notificationText) return 'Notification';
    
    const parts = [];
    let currentText = notification.notificationText;
    
    if (notification.parameters && Array.isArray(notification.parameters)) {
      notification.parameters.forEach((param: any) => {
        const placeholder = `{{${param.key}}}`;
        if (param.key === 'EntryName' && param.target) {
          const split = currentText.split(placeholder);
          if (split.length > 1) {
            parts.push(split[0]);
            parts.push(
              <span 
                key={param.key} 
                style={{ color: 'var(--app-accent-color)', textDecoration: 'underline', cursor: 'pointer' }}
                onClick={(e) => onEntryNameClick(param.target, item.id, e)}
              >
                {param.value}
              </span>
            );
            currentText = split.slice(1).join(placeholder);
            /* Şablon "... {{EntryName}}." iken değer zaten noktayla bitiyorsa çift nokta oluşmasın */
            const valueStr = param.value != null ? String(param.value) : '';
            if (valueStr.trimEnd().endsWith('.') && /^\.\s*/.test(currentText)) {
              currentText = currentText.replace(/^\.\s*/, '');
            }
          }
        } else {
          currentText = currentText.replace(placeholder, param.value || '');
        }
      });
    }
    
    if (currentText) {
      parts.push(currentText);
    }
    
    return parts.length > 0 ? parts : currentText;
  };

  const pad2 = (n: number) => String(n).padStart(2, '0');

  /** Liste satırı: saat ve tarih ayrı span (layout’ta gap ile ayrışır) */
  const getCreatedTimeParts = (dateString: string): { time: string; date: string } | null => {
    const utcDateString = dateString.includes('Z') ? dateString : `${dateString}Z`;
    const d = new Date(utcDateString);
    if (isNaN(d.getTime())) return null;
    const h = pad2(d.getHours());
    const m = pad2(d.getMinutes());
    const day = pad2(d.getDate());
    const month = pad2(d.getMonth() + 1);
    const year = d.getFullYear();
    return { time: `${h}:${m}`, date: `${day}/${month}/${year}` };
  };

  const avatarSrc = getNotificationAvatarSrc(item.parameters);

  return (
    <List.Item
      className={`${styles.notificationResultItem} ${item.isRead ? styles.notificationResultItemRead : ''}`}
    >
      <div className={styles.notificationItemContent}>
        {!item.isRead && (
          <Tooltip title="Mark as read" mouseEnterDelay={0.3}>
            <span
              className={styles.notificationMarkReadTooltipWrap}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className={styles.notificationMarkReadButton}
                aria-label="Mark as read"
                disabled={markingAsRead}
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkAsRead(item.id);
                }}
              >
                <CheckOutlined className={styles.notificationMarkReadIcon} />
              </button>
            </span>
          </Tooltip>
        )}
        <div className={styles.notificationAvatarWrap}>
          <Avatar
            size={32}
            src={avatarSrc}
            icon={<UserOutlined style={{ color: '#F6FFED', fontSize: 14 }} />}
            className={styles.userAvatar}
          />
          {!item.isRead && <span className={styles.notificationAvatarUnreadBadge} aria-hidden />}
        </div>
        <div className={styles.notificationContent}>
          <div
            className={`${styles.notificationTitle} ${item.isRead ? styles.notificationTitleRead : ''}`}
          >
            {renderNotificationText(item)}
          </div>
          <div
            className={`${styles.notificationTimeRow} ${item.isRead ? styles.notificationTimeRowRead : styles.notificationTimeRowUnread}`}
          >
            {(() => {
              const parts = getCreatedTimeParts(item.createdAt);
              if (!parts) return 'Unknown time';
              return (
                <>
                  <span className={styles.notificationTimeClock}>{parts.time}</span>
                  <span className={styles.notificationTimeDate}>{parts.date}</span>
                </>
              );
            })()}
          </div>
        </div>
      </div>
    </List.Item>
  );
};

export default NotificationResultItem; 